/**
 * The documents view's model, over rows of GET /api/organisations/documents:
 * one row per student, `documents` keyed by the three types, each the
 * current version or null.
 *
 * Owns the filters and the summary counts, both computed from the same
 * predicates, so a filter's count is always its number of rows. What counts
 * as on file is the backend's (tracker_documents.py), the same rule as the
 * Overview's "Documents on file" tile.
 */

// `noun` is the label inside a sentence ("Ada's CV", "Ada's cover letter").
export const DOCUMENT_TYPES = [
  { key: 'cv', label: 'CV', noun: 'CV' },
  { key: 'cover_letter', label: 'Cover letter', noun: 'cover letter' },
  { key: 'personal_statement', label: 'Personal statement', noun: 'personal statement' },
];

/** How many of the three a row has on file, 0 to 3. */
export const documentsHeld = (row) => DOCUMENT_TYPES.filter((t) => Boolean(row?.documents?.[t.key])).length;

const lacks = (type) => (row) => !row?.documents?.[type];

// The missing_<type> keys are also the Overview's Document coverage rows:
// each opens the list on its filter (lib/overviewView documentsView).
export const DOCUMENT_FILTERS = [
  { key: 'everyone', label: 'Everyone', test: () => true },
  { key: 'missing', label: 'Missing something', test: (row) => documentsHeld(row) < DOCUMENT_TYPES.length },
  { key: 'missing_cv', label: 'No CV', test: lacks('cv') },
  { key: 'missing_cover_letter', label: 'No cover letter', test: lacks('cover_letter') },
  { key: 'missing_ps', label: 'No personal statement', test: lacks('personal_statement') },
  { key: 'all_three', label: 'All three', test: (row) => documentsHeld(row) === DOCUMENT_TYPES.length },
];

export const DEFAULT_DOCUMENT_FILTER = 'everyone';

/** The filter that shows the students without a document of `type` (a DOCUMENT_TYPES key). */
export const MISSING_FILTER = {
  cv: 'missing_cv',
  cover_letter: 'missing_cover_letter',
  personal_statement: 'missing_ps',
};

export const isDocumentFilter = (key) => DOCUMENT_FILTERS.some((f) => f.key === key);

/** The rows a filter shows; an unknown key shows everyone. */
export function filterDocumentRows(rows, key) {
  const filter = DOCUMENT_FILTERS.find((f) => f.key === key) || DOCUMENT_FILTERS[0];
  return (rows || []).filter(filter.test);
}

/** Per filter key, its number of rows; per type key, the students holding one. */
export function documentCounts(rows) {
  const list = rows || [];
  return {
    filters: Object.fromEntries(DOCUMENT_FILTERS.map((f) => [f.key, list.filter(f.test).length])),
    types: Object.fromEntries(DOCUMENT_TYPES.map((t) => [t.key, list.filter((r) => Boolean(r?.documents?.[t.key])).length])),
  };
}
